import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import HeroBanner from './components/HeroBanner';
import GameCarousel from './components/GameCarousel';
import FilterChips from './components/FilterChips';
import LoadingSkeleton from './components/LoadingSkeleton';
import gameService from '../../utils/gameService';

const GameLibraryDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    genre: null,
    rating: null,
    releaseDate: null,
    tier: null,
    sortBy: 'trending'
  });
  const [gameData, setGameData] = useState({
    continuePlayingGames: [],
    freeGames: [],
    trendingGames: [],
    newReleases: [],
    actionGames: [],
    strategyGames: []
  });
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    const loadGameData = async () => {
      try {
        setIsLoading(true);
        setError('');

        // Load different categories of games
        const [
          continuePlayingResult,
          freeGamesResult,
          trendingResult,
          newReleasesResult,
          actionGamesResult,
          strategyGamesResult
        ] = await Promise.all([
          user ? gameService.getUserGameSessions(user.id) : Promise.resolve({ success: true, data: [] }),
          gameService.getFreeGames(10),
          gameService.getTrendingGames(10),
          gameService.getNewReleases(10),
          gameService.getGamesByCategory('Action', 10),
          gameService.getGamesByCategory('Strategy', 10)
        ]);

        // Check for errors in any of the requests
        const results = [continuePlayingResult, freeGamesResult, trendingResult, newReleasesResult, actionGamesResult, strategyGamesResult];
        const errorResult = results.find(result => !result.success);
        
        if (errorResult) {
          setError(errorResult.error);
          return;
        }

        // Get user favorites if logged in
        let userFavorites = [];
        if (user) {
          const favoritesResult = await gameService.getUserFavorites(user.id);
          if (favoritesResult.success) {
            userFavorites = favoritesResult.data.map(fav => fav.game_id);
          }
        }

        // Transform continue playing data and group by game name to avoid duplicates
        const continuePlayingMap = new Map();
        
        continuePlayingResult.data?.forEach(session => {
          const gameTitle = session.games?.title;
          if (gameTitle && (!continuePlayingMap.has(gameTitle) || 
              new Date(session.created_at) > new Date(continuePlayingMap.get(gameTitle).created_at))) {
            continuePlayingMap.set(gameTitle, session);
          }
        });

        const continuePlayingGames = Array.from(continuePlayingMap.values()).map(session => ({
          id: session.games.id,
          title: session.games.title,
          studio: session.games.gaming_studios?.name || 'Unknown Studio',
          coverImage: session.games.cover_image_url,
          rating: session.games.average_rating,
          genres: session.games.game_category_mappings?.map(mapping => mapping.game_categories?.name) || [],
          playTime: `${session.games.estimated_play_time || 0}h`,
          progress: session.progress_percentage,
          tier: session.games.tier || 'premium',
          isFavorited: userFavorites.includes(session.games.id)
        }));

        // Transform other game data
        const transformGameData = (games) => games?.map(game => ({
          id: game.id,
          title: game.title,
          studio: game.gaming_studios?.name || 'Unknown Studio',
          coverImage: game.cover_image_url,
          rating: game.average_rating,
          genres: game.game_category_mappings?.map(mapping => mapping.game_categories?.name) || [],
          playTime: `${game.estimated_play_time}h`,
          tier: game.tier || 'premium',
          isNew: (Date.now() - new Date(game.published_at)) < (30 * 24 * 60 * 60 * 1000), // Within 30 days
          isFavorited: userFavorites.includes(game.id)
        })) || [];

        setGameData({
          continuePlayingGames,
          freeGames: transformGameData(freeGamesResult.data),
          trendingGames: transformGameData(trendingResult.data),
          newReleases: transformGameData(newReleasesResult.data),
          actionGames: transformGameData(actionGamesResult.data),
          strategyGames: transformGameData(strategyGamesResult.data)
        });

      } catch (err) {
        setError('Failed to load games. Please try again.');
        console.log('Load games error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadGameData();
  }, [user]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters changed:', newFilters);
    // TODO: Implement filter logic to reload games based on filters
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Game Library - Solis Games</title>
          <meta name="description" content="Discover and play unlimited WebGPU games with your Solis Games subscription" />
        </Helmet>
        
        <Header />
        
        <main className="pt-16 pb-20 md:pb-8">
          <div className="container mx-auto px-4 lg:px-8 py-6">
            <LoadingSkeleton type="hero" />
            <div className="mt-8">
              <LoadingSkeleton type="filters" />
              <LoadingSkeleton type="carousel" />
              <LoadingSkeleton type="carousel" />
              <LoadingSkeleton type="carousel" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
        <title>Game Library - Solis Games</title>
        <meta name="description" content="Discover and play unlimited WebGPU games with your Solis Games subscription" />
        </Helmet>
        
        <Header />
        
        <main className="pt-16 pb-20 md:pb-8">
          <div className="container mx-auto px-4 lg:px-8 py-6">
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-destructive mb-4">Failed to Load Games</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Game Library - Solis Games</title>
        <meta name="description" content="Discover and play unlimited WebGPU games with your Solis Games subscription" />
        <meta name="keywords" content="games, streaming, WebGPU, subscription, gaming library" />
      </Helmet>
      
      <Header />
      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="container mx-auto px-4 lg:px-8 py-6">
          {/* Hero Banner */}
          <HeroBanner />
          
          {/* Filter Section */}
          <div className="mt-8">
            <FilterChips onFilterChange={handleFilterChange} />
          </div>
          
          {/* Game Carousels */}
          <div className="space-y-8">
            {/* Continue Playing */}
            {gameData.continuePlayingGames.length > 0 && (
              <GameCarousel
                title="Continue Playing"
                games={gameData.continuePlayingGames}
                cardSize="default"
                showViewAll={false}
              />
            )}
            
            {/* Free Games - Prominent placement */}
            {gameData.freeGames.length > 0 && (
              <div className="relative">
                <div className="flex items-center space-x-3 mb-4">
                  <h2 className="text-2xl font-bold text-foreground">Free to Play</h2>
                  <div className="px-3 py-1 bg-accent/20 text-accent text-sm font-medium rounded-full">
                    No subscription required
                  </div>
                </div>
                <GameCarousel
                  title=""
                  games={gameData.freeGames}
                  cardSize="default"
                  showViewAll={true}
                />
              </div>
            )}
            
            {/* Trending Now */}
            {gameData.trendingGames.length > 0 && (
              <GameCarousel
                title="Trending Now"
                games={gameData.trendingGames}
                cardSize="default"
                showViewAll={true}
              />
            )}
            
            {/* New Releases */}
            {gameData.newReleases.length > 0 && (
              <GameCarousel
                title="New Releases"
                games={gameData.newReleases}
                cardSize="default"
                showViewAll={true}
              />
            )}
            
            {/* Action Games */}
            {gameData.actionGames.length > 0 && (
              <GameCarousel
                title="Action Games"
                games={gameData.actionGames}
                cardSize="default"
                showViewAll={true}
              />
            )}
            
            {/* Strategy Games */}
            {gameData.strategyGames.length > 0 && (
              <GameCarousel
                title="Strategy Games"
                games={gameData.strategyGames}
                cardSize="default"
                showViewAll={true}
              />
            )}
          </div>
          
          {/* Load More Section */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              Who needs a console when you have Solis Games? 
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                Load More Games
              </button>
              <button className="px-6 py-3 border border-border text-foreground rounded-lg hover:bg-muted/50 transition-colors">
                Browse All Categories
              </button>
            </div>
          </div>
          
          {/* Sign in Banner */}
          {!user && (
            <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-sm text-blue-400 text-center">
                🎮 Sign in to access games, personalized recommendations, and continue playing features
              </p>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default GameLibraryDashboard;
